import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  resetActiveSessionForSignOut,
  resumeActiveSessionTrackingIfNeeded,
  useActiveSession,
  type ActiveSessionPhase,
} from '../stores/active-session-store'
import { resetSessionFollowing } from '../stores/session-following-store'
import { useAuthOptional } from '../contexts/auth-context'
import { shouldPromptGeofenceExit } from '@shared/sessions/geofence-exit'
import { formatProviderEtaAway, liveProviderEtaMinutes } from '@shared/sessions/provider-eta'
import { useLiveJourneyPublication } from '../stores/live-journey-store'
import { addActiveSessionNotificationTapListener } from '../features/sessions/utils/active-session-notification'
import { useGeofenceExitNotification } from '../features/sessions/hooks/use-geofence-exit-prompt'
import { addBackgroundLocationNotificationTapListener } from '../services/background-location'
import { formatKm } from '../features/sessions/utils/format'
import { addGeofenceExitRecordActionListener } from '../services/push-notification-service'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { isTerminalCallPhase } from '../stores/voice-call-store'

const PHASE_LABELS: Record<ActiveSessionPhase, string> = {
  travelling: 'On the way',
  arrived: 'Arrived',
  inSession: 'In session',
}

/**
 * Elapsed time in a phase — meaningful for "Arrived" / "In session" only. While
 * travelling the pill shows the live server ETA instead: an elapsed "46 min"
 * next to "On the way" reads as a countdown (2026-10-20: pill said 46 while the
 * journey card's live ETA said 10).
 */
function formatElapsed(sinceMs: number, now: number): string {
  const totalMin = Math.max(0, Math.floor((now - sinceMs) / 60_000))
  if (totalMin < 60) return `${totalMin} min`
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return `${h} h ${String(m).padStart(2, '0')} min`
}

/**
 * Persistent slim "active session" bar pinned at the top of the app chrome —
 * visible on EVERY route (Google-Maps-chip style) whenever a session is
 * travelling / arrived / checked-in, so the provider can never lose track of a
 * running session while messaging etc. Tapping it returns to the session
 * detail. Deliberately NOT dismissable — the whole point is that it can't be
 * missed; it disappears only when the session leaves an active state.
 *
 * Placement: fixed, safe-area-aware, centered (clears both the mobile bottom
 * nav and the desktop rail, which live at the edges). z-[45] keeps it under
 * the soft-lock sheet (z-50) and all call surfaces (z-60+); when the
 * return-to-call pill (same top-center slot) is visible, this bar shifts down
 * one row so both stay readable and tappable.
 *
 * This component also owns the two shell-level side effects that must run
 * exactly once per app load: resuming persisted km tracking after a WebView
 * reload, and the ongoing-notification tap → navigate-to-session wiring.
 */
export function ActiveSessionBanner() {
  const snapshot = useActiveSession()
  const callSession = useVoiceCallSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [now, setNow] = useState(() => Date.now())
  // The live journey the app-wide ping controller publishes — the SAME server
  // ETA the journey card and the ongoing notification show.
  const liveJourney = useLiveJourneyPublication(snapshot?.sessionUuid ?? null)?.journey ?? null

  // After a reload mid-session: restore the persisted track + restart GPS.
  useEffect(() => {
    resumeActiveSessionTrackingIfNeeded()
  }, [])

  // Sign-out / session expiry / signed-out cold start: once auth has resolved
  // and there is no user, tear the active session down — above all the native
  // location service, which must never keep recording a device nobody is
  // signed in to. Covers the cold-start case (persisted snapshot, no prior
  // signed-in → null transition in this mount). Wired here (always mounted,
  // auth-agnostic) rather than inside the auth flow. Do not touch auth-context.
  const auth = useAuthOptional()
  useEffect(() => {
    if (auth && !auth.isLoading && !auth.user) {
      resetActiveSessionForSignOut()
      resetSessionFollowing()
    }
  }, [auth, auth?.isLoading, auth?.user])

  // Account switch without a visible signed-out render (user A → user B): the
  // previous user's session state, follow summary and native live-ping token
  // must go too.
  const lastUserIdRef = useRef<string | null>(null)
  const userId = auth?.user?.userId ?? null
  useEffect(() => {
    if (!userId) return
    const previous = lastUserIdRef.current
    lastUserIdRef.current = userId
    if (previous && previous !== userId) {
      resetActiveSessionForSignOut()
      resetSessionFollowing()
    }
  }, [userId])

  // Ongoing-notification tap → back to the session detail. Two sources: the
  // JS LocalNotifications chip, and (Android, while native km capture runs)
  // the location foreground service's own notification.
  useEffect(() => {
    let removed = false
    const removers: Array<() => void> = []
    const onTap = (sessionUuid: string) => {
      navigate(`/sessions?session=${encodeURIComponent(sessionUuid)}`)
    }
    const adopt = (unsubscribe: () => void) => {
      if (removed) unsubscribe()
      else removers.push(unsubscribe)
    }
    void addActiveSessionNotificationTapListener(onTap).then(adopt)
    void addBackgroundLocationNotificationTapListener(onTap).then(adopt)
    return () => {
      removed = true
      for (const remove of removers) remove()
    }
  }, [navigate])

  // Geofence-exit departure prompt: the local notification fires from here
  // (always mounted) so it reaches the provider whatever route is open; its
  // "Record departure" action deep-links into the session with the one-shot
  // action param, which records the departure at the time they left.
  useGeofenceExitNotification()
  useEffect(() => {
    let removed = false
    let remove: (() => void) | null = null
    void addGeofenceExitRecordActionListener((sessionUuid) => {
      // Literal params (keep in sync with sessions-page.tsx RECORD_DEPARTURE_ACTION):
      // the page is lazy-loaded, so importing its constants here would pull it
      // into the shell bundle.
      navigate(`/sessions?session=${encodeURIComponent(sessionUuid)}&action=record-departure`)
    }).then((unsubscribe) => {
      if (removed) unsubscribe()
      else remove = unsubscribe
    })
    return () => {
      removed = true
      remove?.()
    }
  }, [navigate])

  // Minute-level elapsed display — a 30s tick keeps it honest without churn.
  useEffect(() => {
    if (!snapshot) return
    const id = window.setInterval(() => setNow(Date.now()), 30_000)
    return () => window.clearInterval(id)
  }, [snapshot])

  if (!snapshot) return null

  // Redundant (and covering the session header) when that session's detail is
  // already open — every other route keeps the bar.
  const params = new URLSearchParams(location.search)
  if (location.pathname === '/sessions' && params.get('session') === snapshot.sessionUuid) {
    return null
  }

  const callPillVisible = Boolean(
    callSession &&
      !isTerminalCallPhase(callSession.phase) &&
      callSession.phase !== 'elsewhere' &&
      callSession.userMinimized,
  )

  const km = snapshot.trackingKm != null ? formatKm(snapshot.trackingKm) ?? '0.0 km' : null
  const cancelAsked = snapshot.cancellationRequested === true
  const leftAsked =
    !cancelAsked && snapshot.phase === 'inSession' && shouldPromptGeofenceExit(snapshot.geofenceExit)
  const loud = cancelAsked || leftAsked
  const liveEtaMinutes = snapshot.phase === 'travelling' ? liveProviderEtaMinutes(liveJourney, now) : null
  const phaseParts =
    snapshot.phase === 'travelling'
      ? // Never the elapsed travel time: the live ETA, or just the phase when none is fresh.
        liveEtaMinutes != null
        ? [PHASE_LABELS.travelling, formatProviderEtaAway(liveEtaMinutes)]
        : [PHASE_LABELS.travelling]
      : [PHASE_LABELS[snapshot.phase], formatElapsed(snapshot.phaseStartedAt, now)]
  const parts = cancelAsked
    ? ['Asked to cancel — open session']
    : leftAsked
      ? ["Looks like you've left — record departure?"]
      : phaseParts
  if (km && !loud) parts.push(km)

  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-none fixed inset-x-0 z-[45] flex justify-center px-4 ${
        callPillVisible
          ? 'top-[calc(max(0.5rem,var(--safe-top))+3.25rem)]'
          : 'top-[max(0.5rem,var(--safe-top))]'
      }`}
    >
      <button
        type="button"
        onClick={() => navigate(`/sessions?session=${encodeURIComponent(snapshot.sessionUuid)}`)}
        aria-label={
          cancelAsked
            ? `${snapshot.memberName} asked to cancel this session. Tap to open the session and respond.`
            : leftAsked
              ? `Looks like you've left the session with ${snapshot.memberName}. Tap to open the session and record your departure.`
              : `Active session with ${snapshot.memberName} — ${parts.join(', ')}. Tap to return to the session.`
        }
        className={`pointer-events-auto flex max-w-full items-center gap-2.5 rounded-full px-4 py-2 text-sm font-semibold text-white shadow-lg ring-1 ring-white/10 backdrop-blur transition focus:outline-none focus-visible:ring-2 ${
          cancelAsked
            ? 'bg-red-700/95 hover:bg-red-600 focus-visible:ring-red-300'
            : leftAsked
              ? 'bg-amber-700/95 hover:bg-amber-600 focus-visible:ring-amber-300'
              : 'bg-slate-900/95 hover:bg-slate-800 focus-visible:ring-emerald-400'
        }`}
      >
        <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full ${
              cancelAsked ? 'bg-red-300/80' : leftAsked ? 'bg-amber-200/80' : 'bg-emerald-400/70'
            }`}
          />
          <span
            className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
              cancelAsked ? 'bg-red-300' : leftAsked ? 'bg-amber-200' : 'bg-emerald-400'
            }`}
          />
        </span>
        <span className="truncate">{snapshot.memberName}</span>
        <span className={`shrink-0 tabular-nums ${loud ? 'text-white/90' : 'text-slate-300'}`}>
          {parts.join(' · ')}
        </span>
      </button>
    </div>
  )
}
